import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { hasFeatureAccess, hasProgramAccess as hasProgramAccessLib, type Feature, type PackageTier } from "@/lib/planFeatures";
import { useImpersonation, impersonationPlanName } from "@/contexts/ImpersonationContext";

export interface SubscriptionData {
  subscribed: boolean;
  plan_name: string | null;
  subscription_end: string | null;
  /** Optional lifecycle hint. Only set by admin impersonation today. */
  status?: "active" | "canceled" | "expired";
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionData | null;
  subscriptionLoading: boolean;
  isPremium: boolean;
  planName: string | null;
  /** All active package tiers held by the user (multi-package support). */
  activePackages: PackageTier[];
  /** All program UUIDs the user can train against right now. */
  activeProgramIds: string[];
  hasAccess: (feature: Feature) => boolean;
  hasProgramAccess: (programId: string | null | undefined) => boolean;
  refreshSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscription: null,
  subscriptionLoading: false,
  isPremium: false,
  planName: null,
  activePackages: [],
  activeProgramIds: [],
  hasAccess: () => false,
  hasProgramAccess: () => false,
  refreshSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface ActiveSubRow {
  plan_name: string;
  program_id: string | null;
}

const normalizeTier = (raw: string | null | undefined): PackageTier | null => {
  if (!raw) return null;
  const u = raw.toUpperCase();
  return u === "STARTER" || u === "PREMIUM" || u === "ELITE" ? (u as PackageTier) : null;
};

const withTimeout = async <T,>(promise: PromiseLike<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [realUser, setRealUser] = useState<User | null>(null);
  const [realSession, setRealSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [realSubscription, setRealSubscription] = useState<SubscriptionData | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [activeRows, setActiveRows] = useState<ActiveSubRow[]>([]);

  const { isImpersonating, role: impersonationRole, status: impersonationStatus, endsInDays: impersonationEndsInDays } = useImpersonation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchActiveRows = useCallback(async (uid: string | undefined) => {
    if (!uid) { setActiveRows([]); return; }
    try {
      const { data, error } = await withTimeout(
        supabase
          .from("subscriptions")
          .select("plan_name, program_id")
          .eq("user_id", uid)
          .eq("status", "active"),
        4_000,
      );
      if (!error) setActiveRows((data ?? []) as ActiveSubRow[]);
    } catch {
      setActiveRows([]);
    }
  }, []);

  const fetchSubscription = useCallback(async (currentSession: Session | null) => {
    if (!currentSession) {
      setRealSubscription(null);
      setActiveRows([]);
      return;
    }
    setSubscriptionLoading(true);
    try {
      const res = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });
      if (!res.error && res.data) {
        setRealSubscription(res.data as SubscriptionData);
      }
      await fetchActiveRows(currentSession.user.id);
    } catch {
      // silently fail
    } finally {
      setSubscriptionLoading(false);
    }
  }, [fetchActiveRows]);

  const refreshSubscription = useCallback(async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    await fetchSubscription(currentSession);
  }, [fetchSubscription]);

  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setRealSession(session);
        setRealUser(session?.user ?? null);
        setLoading(false);
        // Oturum kapandıysa veya token yenileme başarısız olduysa state'i temizle ve ana sayfaya dön.
        const sessionLost =
          event === "SIGNED_OUT" ||
          ((event === "TOKEN_REFRESHED" || event === "USER_UPDATED") && !session);
        if (sessionLost || !session) {
          setRealSubscription(null);
          setActiveRows([]);
          queryClient.clear();
          if (event === "SIGNED_OUT" || sessionLost) {
            const path = window.location.pathname;
            if (path !== "/" && path !== "/auth") {
              navigate("/", { replace: true });
            } else if (path === "/auth") {
              navigate("/", { replace: true });
            }
          }
          return;
        }
        setTimeout(() => fetchSubscription(session), 0);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setRealSession(session);
      setRealUser(session?.user ?? null);
      setLoading(false);
      fetchSubscription(session);
    });

    return () => authSubscription.unsubscribe();
  }, [fetchSubscription]);

  useEffect(() => {
    if (!realSession) return;
    const interval = setInterval(() => fetchSubscription(realSession), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [realSession, fetchSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRealSubscription(null);
    setActiveRows([]);
    // Tüm cache'leri temizle (kullanıcıya özel veriler dahil) ve ana sayfaya dön.
    queryClient.clear();
    navigate("/", { replace: true });
  };

  const effective = useMemo(() => {
    if (!isImpersonating) {
      const isPremium = realSubscription?.subscribed === true;
      const activePackages = Array.from(
        new Set(activeRows.map((r) => normalizeTier(r.plan_name)).filter(Boolean) as PackageTier[]),
      );
      const activeProgramIds = Array.from(
        new Set(activeRows.map((r) => r.program_id).filter(Boolean) as string[]),
      );
      return {
        user: realUser,
        session: realSession,
        subscription: realSubscription,
        isPremium,
        planName: isPremium ? realSubscription?.plan_name ?? null : null,
        activePackages,
        activeProgramIds,
      };
    }

    if (impersonationRole === "anonymous") {
      return {
        user: null as User | null,
        session: null as Session | null,
        subscription: null as SubscriptionData | null,
        isPremium: false,
        planName: null as string | null,
        activePackages: [] as PackageTier[],
        activeProgramIds: [] as string[],
      };
    }

    const planName = impersonationPlanName(impersonationRole);
    const endDate = new Date(Date.now() + impersonationEndsInDays * 86400_000).toISOString();
    const isExpired = impersonationStatus === "expired" || impersonationEndsInDays < 0;
    const subscribed = !isExpired;
    const isPaidTier = subscribed && planName === "PREMIUM";
    const tier = normalizeTier(planName);
    return {
      user: realUser,
      session: realSession,
      subscription: {
        subscribed,
        plan_name: subscribed ? planName : null,
        subscription_end: endDate,
        status: isExpired ? "expired" : impersonationStatus,
      } as SubscriptionData,
      isPremium: isPaidTier,
      planName: subscribed ? planName : null,
      activePackages: subscribed && tier ? [tier] : [],
      activeProgramIds: [] as string[],
    };
  }, [isImpersonating, impersonationRole, impersonationStatus, impersonationEndsInDays, realUser, realSession, realSubscription, activeRows]);

  const hasAccess = useCallback(
    (feature: Feature) =>
      effective.activePackages.length > 0
        ? hasFeatureAccess(effective.activePackages, feature)
        : hasFeatureAccess(effective.planName, feature),
    [effective.activePackages, effective.planName],
  );

  const hasProgramAccess = useCallback(
    (programId: string | null | undefined) => hasProgramAccessLib(effective.activeProgramIds, programId),
    [effective.activeProgramIds],
  );

  return (
    <AuthContext.Provider value={{
      user: effective.user,
      session: effective.session,
      loading,
      subscription: effective.subscription,
      subscriptionLoading,
      isPremium: effective.isPremium,
      planName: effective.planName,
      activePackages: effective.activePackages,
      activeProgramIds: effective.activeProgramIds,
      hasAccess,
      hasProgramAccess,
      refreshSubscription,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
